import * as React from 'react'
import moment from 'moment'
import { orderBy } from 'lodash'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { Skeleton, Empty } from 'antd'
import { API, IDiscountBreakdownReportResponse, PaymentFilter } from '@getgreenline/homi-shared'
import { Currency } from '../../../../../components/Currency'
import { PaymentListModal } from '../../../PaymentListModal'
import { parseAndDownloadCSV } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../Track'
import { Alert } from 'antd-v4'
import { ArrowRightOutlined } from '@ant-design/icons'
import { browserHistory } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { dayjs } from '../../../../../utilities/dayjs'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  reportFilterStore?: ReportFilterStore
}

type SortFields = 'discountName' | 'discountPrice' | 'count'

interface State {
  report?: IDiscountBreakdownReportResponse
  selectedDiscountId?: number | null
  sort: { field: SortFields; direction: 'asc' | 'desc' }

  selectedDiscountIds: Array<number | null>
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class DiscountsReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      sort: {
        field: 'discountPrice',
        direction: 'desc',
      },
      selectedDiscountIds: [],
    }
  }

  componentDidMount() {
    this.loadReport()
  }

  loadReport() {
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!
    this.setState({ report: undefined })
    const filter = {
      locationIds: selectedLocationIds,
      startDate: moment(startDate).startOf('day').toDate(),
      endDate: moment(endDate).endOf('day').toDate(),
    }
    const company = this.props.currentCompanyStore!.company!
    Track.startTimer('dashboard:reports_discounts')
    API.getDiscountBreakdown(company.id, filter).then((report) => {
      Track.track('dashboard:reports_discounts', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ report: report })
    })
  }

  toggleSort(field: SortFields) {
    if (this.state.sort.field === field) {
      if (this.state.sort.direction === 'asc') {
        this.setState({
          sort: {
            field: field,
            direction: 'desc',
          },
        })
      } else {
        this.setState({
          sort: {
            field: field,
            direction: 'asc',
          },
        })
      }
    } else {
      this.setState({
        sort: {
          field: field,
          direction: 'desc',
        },
      })
    }
  }

  toggleDiscount = (discountId: number | null) => {
    const { selectedDiscountIds } = this.state
    if (selectedDiscountIds.includes(discountId)) {
      this.setState({ selectedDiscountIds: selectedDiscountIds.filter((id) => id !== discountId) })
    } else {
      this.setState({ selectedDiscountIds: [...selectedDiscountIds, discountId] })
    }
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const report = this.state.report
    const filename = `discounts-report-${startDate}-to-$endDate}`

    if (report) {
      const reportJSON: Array<{
        discount: string
        count: string
        amount: string
      }> = []

      report.data.forEach((discount) => {
        reportJSON.push({
          discount: `${discount.discountName || 'Manually entered discount'}`,
          count: `${discount.count}`,
          amount: `$${(discount.discountPrice / 100).toFixed(2)}`,
        })
      })

      parseAndDownloadCSV(filename, reportJSON)
    }
  }

  toPaymentTypesReport = () => {
    const { reportFilterStore } = this.props
    if (!reportFilterStore) {
      return
    }

    reportFilterStore.setIsFirstLoad(true)
    browserHistory.push({
      pathname: this.paymentTypesReportPath,
      query: {
        startDate: reportFilterStore.startDateString,
        endDate: reportFilterStore.endDateString,
        locationIds: reportFilterStore.selectedLocationIds,
      },
    })
  }

  get paymentTypesReportPath(): string {
    const companyId = this.props.currentCompanyStore?.company?.id

    return Routes.DASHBOARD_PAYMENT_TYPES_REPORT(companyId!)
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const { report, selectedDiscountIds } = this.state
    const { startDate, endDate } = this.props.reportFilterStore!
    const { selectedLocationIds } = this.props.reportFilterStore!

    const helmet = (
      <Helmet>
        <title>Reports | Discounts</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
    )

    if (!report) {
      return (
        <>
          {helmet}
          <Skeleton active />
        </>
      )
    }

    const orderedDiscounts = orderBy(
      report.data,
      [this.state.sort.field],
      [this.state.sort.direction],
    )

    const discountFilter = new PaymentFilter(company.id, 30, 0)
    discountFilter.setStartDate(dayjs(startDate))
    discountFilter.setEndDate(dayjs(endDate))
    if (this.state.selectedDiscountId !== undefined) {
      discountFilter.setDiscountId(this.state.selectedDiscountId)
    }
    if (selectedLocationIds.length > 0) {
      discountFilter.setLocationIds(selectedLocationIds)
    }

    return (
      <>
        {helmet}
        <ReportFilter maxRangeMonths={12} />
        <Alert
          className='mb-2'
          message={
            <a
              style={{ color: 'black', textDecorationLine: 'underline' }}
              onClick={this.toPaymentTypesReport}
            >
              Go to <strong>Payment Types</strong> report to see loyalty redeemed{' '}
              <ArrowRightOutlined />
            </a>
          }
          type='success'
        />
        <div>
          {!report.data.length ? (
            <Empty description='No discount sales available in this time period' />
          ) : (
            <div className='row'>
              <div className='col-md-12'>
                <div className='rounded background-white'>
                  <div className='card-body table-responsive'>
                    <div>
                      <b>Discounts</b>
                    </div>
                    <div className='text-muted'>Aggregated</div>

                    <div className='mt-2 mb-2'>
                      <button className='btn btn-sm btn-light' onClick={() => this.export()}>
                        Export as csv
                      </button>
                    </div>
                    {
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>
                              <a onClick={() => this.toggleSort('discountName')}>Discount</a>
                            </th>
                            <th className='text-right'>
                              <a onClick={() => this.toggleSort('count')}>Count</a>
                            </th>
                            <th className='text-right'>
                              <a onClick={() => this.toggleSort('discountPrice')}>Amount</a>
                            </th>
                            <th className='text-right text-muted'>Expand</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderedDiscounts.map((discountData) => {
                            const isSelected = selectedDiscountIds.includes(discountData.discountId)

                            return (
                              <React.Fragment key={discountData.discountId || ''}>
                                <tr>
                                  <td>
                                    <a
                                      onClick={() => {
                                        this.setState({
                                          selectedDiscountId: discountData.discountId,
                                        })
                                      }}
                                    >
                                      {discountData.discountName || (
                                        <span>Manually entered discount</span>
                                      )}
                                    </a>
                                  </td>
                                  <td className='text-right'>{discountData.count}</td>
                                  <td className='text-right'>
                                    <Currency centValue={discountData.discountPrice} />
                                  </td>
                                  <td className='text-right'>
                                    <a onClick={() => this.toggleDiscount(discountData.discountId)}>
                                      <span
                                        className={`fa ${
                                          isSelected ? 'fa-chevron-up' : 'fa-chevron-down'
                                        }`}
                                      />
                                    </a>
                                  </td>
                                </tr>

                                {isSelected &&
                                  discountData.subDiscounts.map((subDiscount, i) => {
                                    return (
                                      <tr
                                        key={`${discountData.discountId || 'manual'} - ${i}`}
                                        style={{ backgroundColor: `rgb(252, 248, 227)` }}
                                      >
                                        <td>
                                          {subDiscount.employeeName || <span>No employee</span>}
                                        </td>
                                        <td className='text-right'>{subDiscount.count}</td>
                                        <td className='text-right'>
                                          <Currency centValue={subDiscount.discountPrice} />
                                        </td>
                                        <td></td>
                                      </tr>
                                    )
                                  })}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
          {this.state.selectedDiscountId !== undefined && (
            <PaymentListModal
              filter={discountFilter}
              onClose={() => this.setState({ selectedDiscountId: undefined })}
            />
          )}
        </div>
      </>
    )
  }
}
